import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPublishedPosts } from "../../api/blogAPI";
import { getSettings } from "../../api/settingsAPI";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

// ════════════════════════════════════════════════════════════════════════════
// CLASSIC — compact dark list with thumbnail (original look)
// ════════════════════════════════════════════════════════════════════════════
const ClassicLayout = ({ posts }) => (
  <div className="min-h-screen bg-[#0d0d10]">
    <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.05)_0%,transparent_60%)] pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-semibold transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </Link>
        <div className="flex-1 h-px bg-zinc-800" />
      </div>
      <div className="mb-8">
        <h1 className="text-3xl font-black text-white tracking-tight">Blog</h1>
        <p className="text-zinc-500 text-sm mt-1">Thoughts, notes, and things I find interesting.</p>
      </div>
      {posts.length === 0 ? (
        <EmptyState dark />
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const cover = resolveImg(post.cover_img);
            return (
              <Link key={post.id} to={`/blog/${post.slug}`}
                className="group flex gap-5 bg-[#111116] border border-zinc-800 hover:border-blue-600/40 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-900/10">
                {cover && (
                  <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-zinc-800">
                    <img src={cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <h2 className="text-white font-bold text-base leading-snug group-hover:text-blue-300 transition-colors line-clamp-2">{post.title}</h2>
                    {post.excerpt && <p className="text-zinc-400 text-sm mt-1 leading-relaxed line-clamp-2">{post.excerpt}</p>}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    {post.published_at && <span className="text-xs text-zinc-600 font-mono">{fmtDate(post.published_at)}</span>}
                    {post.view_count != null && <><span className="text-zinc-700">·</span><span className="text-xs text-zinc-600">{post.view_count} views</span></>}
                    <span className="ml-auto flex items-center gap-1 text-blue-400 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      Read more
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MODERN — magazine grid, featured hero post + card grid, purple
// ════════════════════════════════════════════════════════════════════════════
const ModernLayout = ({ posts }) => {
  const [featured, ...rest] = posts;
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-semibold transition-colors mb-10">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to home
        </Link>
        <div className="flex flex-col items-center text-center mb-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8 bg-purple-500" />
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-400">The Blog</span>
            <div className="h-px w-8 bg-purple-500" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Latest <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">Articles</span></h1>
        </div>

        {posts.length === 0 ? <EmptyState dark /> : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`}
                className="group grid md:grid-cols-2 gap-8 mb-14 bg-white/3 border border-white/8 hover:border-purple-500/30 rounded-3xl overflow-hidden transition-all duration-200">
                <div className="h-56 md:h-full overflow-hidden bg-slate-900">
                  {resolveImg(featured.cover_img) ? (
                    <img src={resolveImg(featured.cover_img)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-slate-900 flex items-center justify-center text-purple-700">
                      <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-10 flex flex-col justify-center">
                  <span className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-3">Featured</span>
                  <h2 className="text-2xl font-black leading-tight group-hover:text-purple-300 transition-colors mb-3">{featured.title}</h2>
                  {featured.excerpt && <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-4">{featured.excerpt}</p>}
                  <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
                    {fmtDate(featured.published_at)}
                    {featured.view_count != null && <><span>·</span>{featured.view_count} views</>}
                  </div>
                </div>
              </Link>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post) => {
                const cover = resolveImg(post.cover_img);
                return (
                  <Link key={post.id} to={`/blog/${post.slug}`}
                    className="group flex flex-col bg-white/3 border border-white/8 hover:border-purple-500/30 rounded-2xl overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-purple-900/20">
                    <div className="h-40 overflow-hidden bg-slate-900">
                      {cover ? <img src={cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        : <div className="w-full h-full bg-gradient-to-br from-purple-900/30 to-slate-900" />}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-bold text-sm leading-snug group-hover:text-purple-300 transition-colors line-clamp-2 mb-2">{post.title}</h3>
                      {post.excerpt && <p className="text-zinc-400 text-xs leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>}
                      <span className="text-[10px] text-zinc-500 font-mono mt-4 pt-4 border-t border-white/5">{fmtDate(post.published_at)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TERMINAL — directory listing / file browser, green-on-black
// ════════════════════════════════════════════════════════════════════════════
const TerminalLayout = ({ posts }) => (
  <div className="min-h-screen bg-black text-green-400 font-mono relative">
    <div className="fixed inset-0 pointer-events-none z-40 opacity-[0.07]"
      style={{ backgroundImage: "repeating-linear-gradient(0deg, #22c55e 0px, #22c55e 1px, transparent 1px, transparent 3px)" }} />
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <Link to="/" className="inline-flex items-center gap-1 text-green-600 hover:text-green-400 text-sm mb-6 transition-colors">
        <span className="text-green-800">$</span> cd ~/
      </Link>
      <div className="rounded-lg border border-green-500/30 bg-[#0a0e0a] overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0d130d] border-b border-green-500/20">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          <span className="ml-2 text-[11px] text-green-700">affan@portfolio: ~/blog</span>
        </div>
        <div className="p-4 sm:p-5">
          <p className="text-green-500 text-sm mb-1">
            <span className="text-green-700">affan@portfolio</span><span className="text-green-800">:~/blog$ </span>ls -la
          </p>
          {posts.length === 0 ? (
            <p className="text-green-700 text-sm mt-3">total 0 — no posts found.</p>
          ) : (
            <>
              <p className="text-green-700 text-xs mt-2 mb-2">total {posts.length}</p>
              <div className="space-y-0.5">
                {posts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`}
                    className="group grid grid-cols-[auto_auto_1fr] sm:grid-cols-[auto_auto_auto_1fr] items-center gap-x-3 text-xs hover:bg-green-500/10 px-2 py-1.5 rounded transition-colors">
                    <span className="text-green-800 hidden sm:inline">-rw-r--r--</span>
                    <span className="text-green-700">{(post.view_count ?? 0).toString().padStart(4, " ")}</span>
                    <span className="text-green-700 hidden sm:inline">{post.published_at ? new Date(post.published_at).toLocaleDateString("en-CA") : "----------"}</span>
                    <span className="text-green-400 group-hover:text-green-300 truncate">
                      {post.slug}.md
                      <span className="text-green-800 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">→ cat</span>
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}
          <p className="text-green-500 text-sm mt-4">
            <span className="text-green-700">affan@portfolio</span><span className="text-green-800">:~/blog$ </span>
            <span className="inline-block w-2 h-4 bg-green-400 align-middle animate-pulse" />
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// FABRICA — editorial magazine, warm off-white, orange accent
// ════════════════════════════════════════════════════════════════════════════
const FabricaLayout = ({ posts }) => {
  const [featured, ...rest] = posts;
  const ACCENT = "#E85D04";
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAFAF8" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-stone-400 hover:text-stone-800 text-sm font-semibold transition-colors mb-10"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to home
        </Link>

        {/* Header */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-stone-300" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-stone-400">Writing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-stone-900 tracking-tight">
            The <span style={{ color: ACCENT }}>Blog</span>
          </h1>
          <p className="text-stone-500 mt-3">Thoughts, notes, and things I find interesting.</p>
        </div>

        {posts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Featured post — wide card */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="group grid md:grid-cols-[3fr_2fr] gap-0 mb-12 bg-white border border-stone-200 hover:border-stone-400 rounded-2xl overflow-hidden transition-all hover:shadow-lg"
              >
                <div className="h-56 md:h-72 overflow-hidden bg-stone-100">
                  {resolveImg(featured.cover_img) ? (
                    <img src={resolveImg(featured.cover_img)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: ACCENT + "15" }}>
                      <svg className="w-16 h-16" style={{ color: ACCENT + "50" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-7 md:p-10 flex flex-col justify-center" style={{ backgroundColor: "#FAFAF8" }}>
                  <span
                    className="text-[10px] font-black uppercase tracking-[0.3em] mb-3"
                    style={{ color: ACCENT }}
                  >
                    Latest
                  </span>
                  <h2 className="text-xl sm:text-2xl font-black text-stone-900 leading-tight group-hover:opacity-70 transition-opacity mb-3">
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="text-stone-500 text-sm leading-relaxed line-clamp-3 mb-4">{featured.excerpt}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-stone-400">
                    {fmtDate(featured.published_at)}
                    {featured.view_count != null && <><span>·</span>{featured.view_count} views</>}
                  </div>
                </div>
              </Link>
            )}

            {/* Rest — 3-col grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post) => {
                const cover = resolveImg(post.cover_img);
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex flex-col bg-white border border-stone-200 hover:border-stone-400 rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="h-44 overflow-hidden bg-stone-100">
                      {cover ? (
                        <img src={cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full" style={{ backgroundColor: ACCENT + "10" }} />
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-stone-900 font-black text-sm leading-snug line-clamp-2 mb-2 group-hover:opacity-70 transition-opacity">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-stone-500 text-xs leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
                      )}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-100">
                        <span className="text-[11px] text-stone-400">{fmtDate(post.published_at)}</span>
                        <span
                          className="ml-auto text-xs font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ color: ACCENT }}
                        >
                          Read
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AURORA — dark cinematic, green accent, glassmorphism
// ════════════════════════════════════════════════════════════════════════════
const AuroraLayout = ({ posts }) => {
  const BG = "#020617"; const ACCENT = "#22C55E"; const MUTED = "#94A3B8";
  const BORDER = "rgba(148,163,184,0.12)"; const SURFACE = "rgba(15,23,42,0.7)";
  const fmtD = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : null;
  const [featured, ...rest] = posts;
  return (
    <div style={{ minHeight:"100vh", background:BG, color:"#F8FAFC", fontFamily:"'DM Sans', sans-serif", padding:"80px 24px" }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>
        <Link to="/" style={{ display:"inline-flex", alignItems:"center", gap:8, color:MUTED, textDecoration:"none", fontSize:13, fontWeight:600, marginBottom:48 }}>
          <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to home
        </Link>
        <div style={{ marginBottom:48 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
            <div style={{ height:1, width:32, background:ACCENT }} />
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.3em", textTransform:"uppercase", color:ACCENT }}>Writing</span>
          </div>
          <h1 style={{ fontFamily:"'Space Grotesk', sans-serif", fontWeight:700, fontSize:"clamp(32px,6vw,56px)", lineHeight:1.1 }}>
            The <span style={{ color:ACCENT }}>Blog</span>
          </h1>
        </div>
        {posts.length === 0 ? (
          <div style={{ padding:"80px 0", textAlign:"center", color:MUTED, fontSize:14 }}>No posts yet.</div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} style={{ textDecoration:"none", display:"block", marginBottom:40 }}>
                <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:20, overflow:"hidden",
                  display:"grid", gridTemplateColumns:"2fr 1fr", backdropFilter:"blur(12px)",
                  transition:"border-color 0.3s", cursor:"pointer" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(34,197,94,0.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}>
                  <div style={{ height:280, overflow:"hidden", background:"rgba(34,197,94,0.05)" }}>
                    {resolveImg(featured.cover_img)
                      ? <img src={resolveImg(featured.cover_img)} alt={featured.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", color:`${ACCENT}30`, fontSize:64 }}>✍</div>}
                  </div>
                  <div style={{ padding:"32px 28px", display:"flex", flexDirection:"column", justifyContent:"center" }}>
                    <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.3em", textTransform:"uppercase", color:ACCENT, marginBottom:12 }}>Latest</span>
                    <h2 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:20, fontWeight:700, lineHeight:1.4, marginBottom:12 }}>{featured.title}</h2>
                    {featured.excerpt && <p style={{ fontSize:13, color:MUTED, lineHeight:1.7, marginBottom:16, display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{featured.excerpt}</p>}
                    <span style={{ fontSize:11, color:MUTED }}>{fmtD(featured.published_at)}</span>
                  </div>
                </div>
              </Link>
            )}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:20 }}>
              {rest.map(post => {
                const cover = resolveImg(post.cover_img);
                return (
                  <Link key={post.id} to={`/blog/${post.slug}`} style={{ textDecoration:"none" }}>
                    <div style={{ background:SURFACE, border:`1px solid ${BORDER}`, borderRadius:16, overflow:"hidden",
                      backdropFilter:"blur(12px)", transition:"all 0.3s ease", cursor:"pointer", height:"100%" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(34,197,94,0.3)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.transform = "none"; }}>
                      {cover && <div style={{ height:160, overflow:"hidden" }}><img src={cover} alt={post.title} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover" }} /></div>}
                      <div style={{ padding:"18px 20px" }}>
                        <h3 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:15, fontWeight:600, lineHeight:1.5, marginBottom:8 }}>{post.title}</h3>
                        {post.excerpt && <p style={{ fontSize:12, color:MUTED, lineHeight:1.7, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden", marginBottom:12 }}>{post.excerpt}</p>}
                        <div style={{ display:"flex", justifyContent:"space-between" }}>
                          <span style={{ fontSize:11, color:`${MUTED}80` }}>{fmtD(post.published_at)}</span>
                          <span style={{ fontSize:12, color:ACCENT, fontWeight:600 }}>Read →</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ─── Shared empty state ───────────────────────────────────────────────────────
const EmptyState = ({ dark }) => (
  <div className={`py-20 flex flex-col items-center ${dark ? "text-zinc-600" : "text-gray-400"}`}>
    <svg className="w-10 h-10 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
    <p className="text-sm font-semibold">No posts yet</p>
  </div>
);

// ─── Root ─────────────────────────────────────────────────────────────────────
const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    Promise.all([
      getPublishedPosts().catch(() => []),
      getSettings().then((s) => s.active_theme ?? "classic").catch(() => "classic"),
    ]).then(([p, t]) => {
      setPosts(Array.isArray(p) ? p : []);
      setTheme(t);
    });
  }, []);

  if (theme === null) {
    return (
      <div className="min-h-screen bg-[#0d0d10] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (theme === "modern") return <ModernLayout posts={posts} />;
  if (theme === "terminal") return <TerminalLayout posts={posts} />;
  if (theme === "fabrica") return <FabricaLayout posts={posts} />;
  if (theme === "aurora") return <AuroraLayout posts={posts} />;
  return <ClassicLayout posts={posts} />;
};

export default BlogList;
